import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import { ShieldCheck, PaperPlaneTilt, Warning, EnvelopeSimple, User } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Bootstrap() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [needs, setNeeds] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/bootstrap-status`);
        setNeeds(!!data.needs_bootstrap);
        if (!data.needs_bootstrap) {
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        }
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally { setChecking(false); }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/bootstrap`, {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
      });
      setDone(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  if (checking) {
    return <Wrapper><p className="text-sm text-slate-500">Checking&hellip;</p></Wrapper>;
  }
  if (!needs) {
    return (
      <Wrapper>
        <Warning size={44} weight="duotone" className="text-amber-500 mx-auto mb-3" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy mb-2">Already configured</h1>
        <p className="text-sm text-slate-500">Redirecting to sign-in&hellip;</p>
      </Wrapper>
    );
  }
  if (done) {
    return (
      <Wrapper>
        <PaperPlaneTilt size={44} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy mb-2" data-testid="bootstrap-success">
          Admin created
        </h1>
        <p className="text-sm text-slate-600 mb-4">
          Check the inbox at <strong className="text-brand-navy">{done.email}</strong> for your activation link.
        </p>
        {done._activation_url && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-700 font-semibold mb-1">
              &#9888; SMTP not configured yet
            </div>
            <p className="text-xs text-amber-800 mb-2">
              Open this URL once to activate the account. Save it now &mdash; it won&apos;t be shown again.
            </p>
            <code className="block text-[10px] font-mono text-brand-navy break-all bg-white border border-amber-200 rounded p-2"
              data-testid="bootstrap-activation-url">
              {done._activation_url}
            </code>
            <a href={done._activation_url}
              className="inline-block mt-3 text-sm font-semibold text-brand-navy hover:text-brand-cyan underline underline-offset-4">
              Click to activate &rarr;
            </a>
          </div>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ShieldCheck size={44} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
      <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan font-semibold mb-1">
        First-run setup
      </div>
      <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy mb-2">
        Create the first admin
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        This page is only available before any user has signed up.
        You&apos;ll receive a magic-link email to activate.
      </p>
      <form onSubmit={submit} className="space-y-4 text-left" data-testid="bootstrap-form">
        <Field label="Your name" icon={User}>
          <input required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="bootstrap-name-input"
            placeholder="Jane Smith" />
        </Field>
        <Field label="Your work email" icon={EnvelopeSimple}>
          <input type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="bootstrap-email-input"
            placeholder="you@yourcompany.com" />
        </Field>
        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5"
            data-testid="bootstrap-error">{error}</div>
        )}
        <button type="submit" disabled={loading}
          data-testid="bootstrap-submit-btn"
          className="w-full bg-brand-navy hover:bg-brand-navy/90 disabled:opacity-60 rounded-lg px-4 py-3 text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2">
          <PaperPlaneTilt size={16} weight="bold" />
          {loading ? "Creating account…" : "Create admin & send activation link"}
        </button>
      </form>
    </Wrapper>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-navy mb-2">{label}</label>
      <div className="relative">
        {Icon && <Icon size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
        {React.cloneElement(children, {
          className: `w-full bg-white border border-slate-300 rounded-lg ${Icon ? "pl-10 pr-3" : "px-3"} py-2.5 text-sm text-brand-navy placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 transition-all`,
        })}
      </div>
    </div>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-brand-navy px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,199,255,0.10) 0%, transparent 60%)" }} />
      </div>
      <div className="relative w-full max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="bootstrap-card"
          style={{ boxShadow: "0 25px 60px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)" }}>
          <div className="bg-brand-navy px-8 py-8 flex items-center justify-center">
            <Logo size={100} />
          </div>
          <div className="px-8 pt-10 pb-8 text-center">
            {children}
          </div>
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-center gap-2">
            <ShieldCheck size={14} weight="fill" className="text-brand-navy" />
            <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">
              Encrypted &middot; Audited &middot; Single-use
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
